import { useEffect, useMemo, useState } from 'react';
import {
    addDoc,
    collection,
    deleteDoc,
    doc,
    getDoc,
    onSnapshot,
    query,
    setDoc,
    where
} from 'firebase/firestore';
import { db } from '../config/fb-config';

const EMPTY_CONSTRAINTS = [];

const withId = (snapshot) => ({
    id: snapshot.id,
    ...snapshot.data()
});

export const getDocument = async (collectionName, docId, includeId = true) => {
    const snapshot = await getDoc(doc(db, collectionName, docId));
    if (!snapshot.exists()) {
        return null;
    }
    return includeId ? withId(snapshot) : snapshot.data();
};

export const addDocument = (collectionName, payload) => {
    return addDoc(collection(db, collectionName), payload);
};

export const setDocument = (collectionName, docId, payload) => {
    return setDoc(doc(db, collectionName, docId), payload);
};

export const deleteDocument = (collectionName, docId) => {
    return deleteDoc(doc(db, collectionName, docId));
};

export const useCollection = (collectionName, constraints = EMPTY_CONSTRAINTS) => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const collectionRef = collection(db, collectionName);
        const queryRef = constraints.length ? query(collectionRef, ...constraints) : collectionRef;

        const unsubscribe = onSnapshot(queryRef, (snapshot) => {
            setData(snapshot.docs.map(withId));
            setLoading(false);
        }, (err) => {
            setError(err);
            setLoading(false);
        });

        return unsubscribe;
    }, [collectionName, constraints]);

    return { data, loading, error };
};

export const useDocument = (collectionName, docId, includeId = true) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const unsubscribe = onSnapshot(doc(db, collectionName, docId), (snapshot) => {
            if (!snapshot.exists()) {
                setData(null);
            } else {
                setData(includeId ? withId(snapshot) : snapshot.data());
            }
            setLoading(false);
        }, (err) => {
            setError(err);
            setLoading(false);
        });

        return unsubscribe;
    }, [collectionName, docId, includeId]);

    return { data, loading, error };
};

export const useVisibleProducts = () => {
    const constraints = useMemo(() => [where('hidden', '==', false)], []);
    return useCollection('products', constraints);
};
